/*  ────────────────────────────────────────────────────────────────
    components/members/ReviewsSection.tsx
    • reads detailed reviews from **user_reviews**
    • summary numbers from **user_ratings**
    • completely decoupled from legacy activities table
   ──────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import ReviewList from '../reviews/ReviewList';
import ReviewStatsDisplay from '../reviews/ReviewStats';
import type { Review, ReviewStats } from '../../types/review';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface ReviewsSectionProps {
  memberId: string;   // auth_id of the member whose profile we’re viewing
  memberName: string;
  sports: string[];
}

const fallbackUrl =
  'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ memberId }) => {
  const [reviews, setReviews]   = useState<Review[]>([]);
  const [stats,   setStats]     = useState<ReviewStats | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState<string | null>(null);

  /* ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!memberId) return;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        /* 1. get internal users.id primary-key */
        const { data: userRow, error: uErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', memberId)
          .single();

        if (uErr) throw uErr;
        if (!userRow) throw new Error('User not found');
        const userPk = userRow.id;

        /* 2. detailed reviews */
        const { data: revRows, error: rErr } = await supabase
          .from('user_reviews')
          .select(`
            id,
            rating_overall,
            feedback,
            created_at,
            categories,
            author:author_id ( auth_id, full_name, avatar_url ),
            activity:activity_id ( sport, venue_city )
          `)
          .eq('target_id', userPk)
          .order('created_at', { ascending: false });

        if (rErr) throw rErr;

        const mapped: Review[] =
          revRows?.map(row => ({
            id:           row.id,
            authorId:     row.author.auth_id,
            authorName:   row.author.full_name,
            authorImage:  row.author.avatar_url || fallbackUrl,
            targetId:     memberId,
            rating:       parseFloat(row.rating_overall),
            content:      row.feedback ?? '',
            timestamp:    row.created_at,
            likes:        0,
            sport:        row.activity?.sport ?? '',
            venue:        row.activity?.venue_city ?? '',
            isVerified:   true
          })) ?? [];

        setReviews(mapped);

        /* 3. aggregate stats */
        const { data: statRow, error: sErr } = await supabase
          .from('user_ratings')
          .select('*')
          .eq('user_id', userPk)
          .maybeSingle();

        if (sErr) throw sErr;

        setStats(
          statRow
            ? {
                averageRating:    parseFloat(statRow.overall_rating)   || 0,
                totalReviews:     parseInt(statRow.total_ratings, 10) || 0,
                avgPunctuality:   parseFloat(statRow.avg_punctuality) || 0,
                avgSportsmanship: parseFloat(statRow.avg_sportsmanship) || 0,
                avgSkillMatch:    parseFloat(statRow.avg_skill_match) || 0,
                avgCommunication: parseFloat(statRow.avg_communication) || 0,
                totalRatings:     parseInt(statRow.total_ratings, 10) || 0
              }
            : {
                averageRating: 0,
                totalReviews: 0,
                avgPunctuality: 0,
                avgSportsmanship: 0,
                avgSkillMatch: 0,
                avgCommunication: 0,
                totalRatings: 0
              }
        );
      } catch (err) {
        console.error('Error loading member reviews:', err);
        setError(err instanceof Error ? err.message : 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    })();
  }, [memberId]);

  /* ────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {error ? (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl border border-red-500/20">
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm underline mt-2 hover:text-red-400"
          >
            Try Again
          </button>
        </div>
      ) : loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-dark-300 rounded-xl" />
          <div className="h-24 bg-dark-300 rounded-xl" />
        </div>
      ) : stats && stats.totalRatings > 0 ? (
        <>
          <ReviewStatsDisplay stats={stats} />

          {reviews.length ? (
            <ReviewList
              reviews={reviews}
              onLike={() => {}}
              onReport={() => {}}
            />
          ) : (
            <div
              className={cn(
                'text-center py-8 bg-dark-300 rounded-xl border border-dark-400',
                'mt-6'
              )}
            >
              <p className="text-dark-800">No detailed reviews available yet</p>
            </div>
          )}
        </>
      ) : (
        <div
          className={cn(
            'text-center py-12 bg-dark-300 rounded-xl border border-dark-400',
            'animate-in fade-in-50 duration-500'
          )}
        >
          <Star className="h-12 w-12 text-dark-800 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            No Ratings Yet
          </h3>
          <p className="text-dark-800 max-w-sm mx-auto">
            Complete activities to receive ratings from other members
          </p>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;